import React from 'react'

import type { MessageBubbleVariant } from '../../types'

/**
 * Canonical chat-bubble tail (20×23). Kept identical to the in-channel tail
 * drawn by `CustomMessage` in `styles.css` so standalone previews and real
 * messages share one silhouette — the single source of truth for the shape.
 *
 * The path points to the bottom-*right*; the `received` variant mirrors it
 * with `scaleX(-1)`, matching the in-channel convention.
 */
const TAIL_MASK =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='23' fill='none'%3E%3Cpath fill='black' d='M0 0H15C15 10.1934 15.859 15.2345 19.168 21.4893C19.527 22.168 19.039 22.9917 18.274 22.9178C5.176 21.6506 0.32 6.0737 0 0Z'/%3E%3C/svg%3E\") no-repeat center / 100% 100%"

/**
 * Bubble surface colours, reused from the toolkit's Stream theme vars so the
 * tail is always tinted the same colour as its bubble. Those vars are declared
 * on `.str-chat`, so we supply the canonical values as `var()` fallbacks —
 * that keeps the tail correct when rendered *outside* a channel (e.g. a
 * standalone preview) while still honouring the var (or a consumer override)
 * wherever it's defined.
 */
const BG_VAR_BY_VARIANT: Record<MessageBubbleVariant, string> = {
  sent: 'var(--str-chat__own-message-bubble-background-color, #1e2330)',
  received: 'var(--str-chat__message-bubble-background-color, #f1f0ee)',
}

/**
 * The tail element. Absolutely positioned at the bubble's bottom corner and
 * painted behind the bubble body (`z-index: -1`), so the parent bubble must be
 * `position: relative` with `isolation: isolate` (see {@link BubbleShell}).
 */
export const BubbleTail = ({ variant }: { variant: MessageBubbleVariant }) => (
  <span
    aria-hidden="true"
    className="pointer-events-none absolute bottom-0 z-[-1] h-[1.4375rem] w-5"
    style={{
      insetInlineEnd: variant === 'sent' ? 'calc(-1 * 0.3125rem)' : undefined,
      insetInlineStart:
        variant === 'received' ? 'calc(-1 * 0.3125rem)' : undefined,
      transform: variant === 'received' ? 'scaleX(-1)' : undefined,
      transformOrigin: 'center',
      backgroundColor: BG_VAR_BY_VARIANT[variant],
      WebkitMask: TAIL_MASK,
      mask: TAIL_MASK,
    }}
  />
)
